import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  DataTableHeader,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { ProjectGanttTask, ProjectPhase } from '../types';
import { MODE_CHIP, MODE_LABEL, fmtDate, isTaskDone, isTaskOverdue, statusChip } from './workspaceHelpers';

export interface ProjectTasksTableProps {
  tasks: ProjectGanttTask[];
  phases: ProjectPhase[];
  /** Abre o drawer da tarefa. */
  onSelectTask: (task: ProjectGanttTask) => void;
}

/**
 * Aba "Lista" (protótipo G6) — tabela de tarefas com fase, responsável, prazos,
 * dependências e status. Linha clicável abre o drawer; o número do ticket leva
 * ao helpdesk. Legenda dos modos de dependência no rodapé.
 */
export const ProjectTasksTable: React.FC<ProjectTasksTableProps> = ({ tasks, phases, onSelectTask }) => {
  const navigate = useNavigate();

  const phaseById = useMemo(() => {
    const map = new Map<string, ProjectPhase>();
    phases.forEach((p) => map.set(p.id, p));
    return map;
  }, [phases]);

  const taskById = useMemo(() => {
    const map = new Map<string, ProjectGanttTask>();
    tasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [tasks]);

  if (tasks.length === 0) {
    return (
      <div
        className="rounded-lg border p-8 text-center"
        style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
        data-testid="project-ws-tasks-empty"
      >
        Nenhuma tarefa neste projeto ainda.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="project-ws-tasks-table">
      <DataTable>
        <DataTableHeader>
          <DataTableHeadCell>Tarefa</DataTableHeadCell>
          <DataTableHeadCell>Ticket</DataTableHeadCell>
          <DataTableHeadCell>Fase</DataTableHeadCell>
          <DataTableHeadCell>Responsável</DataTableHeadCell>
          <DataTableHeadCell>Início</DataTableHeadCell>
          <DataTableHeadCell>Fim</DataTableHeadCell>
          <DataTableHeadCell>Dependências</DataTableHeadCell>
          <DataTableHeadCell>Status</DataTableHeadCell>
        </DataTableHeader>
        <DataTableBody>
          {tasks.map((task) => {
            const phase = task.phaseId ? phaseById.get(task.phaseId) : undefined;
            const chip = statusChip(task.ticket?.statusCategory);
            const overdue = isTaskOverdue(task);
            const done = isTaskDone(task);
            const deps = task.dependencies ?? [];

            return (
              <DataTableRow
                key={task.id}
                className="cursor-pointer"
                onClick={() => onSelectTask(task)}
                data-testid={`project-ws-tasks-row-${task.id}`}
              >
                <DataTableCell>
                  <div className="flex items-center gap-1.5">
                    {task.isMilestone && <span style={{ color: '#8b5cf6' }}>◆</span>}
                    <span
                      className="text-sm font-medium"
                      style={{
                        color: 'var(--theme-card-text)',
                        textDecoration: done ? 'line-through' : 'none',
                        opacity: done ? 0.65 : 1,
                      }}
                    >
                      {task.ticket?.title || 'Tarefa'}
                    </span>
                  </div>
                </DataTableCell>

                <DataTableCell>
                  {task.ticket?.ticketId ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/helpdesk/tickets/${task.ticket?.ticketId}`);
                      }}
                      className="text-xs font-mono transition-opacity hover:opacity-80 inline-flex items-center gap-1"
                      style={{ color: 'var(--theme-primary)' }}
                      data-testid={`project-ws-tasks-ticket-${task.id}`}
                    >
                      #{task.ticket.ticketNumber}
                      <Icon name="external-link" className="w-3 h-3" />
                    </button>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
                  )}
                </DataTableCell>

                <DataTableCell>
                  {phase ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium"
                      style={{ fontSize: 11, backgroundColor: `${phase.color}1f`, color: phase.color }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phase.color }} />
                      {phase.name}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
                  )}
                </DataTableCell>

                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                    {task.ticket?.assigneeName || '—'}
                  </span>
                </DataTableCell>

                <DataTableCell>
                  <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    {fmtDate(task.startDate)}
                  </span>
                </DataTableCell>

                <DataTableCell>
                  <span className="text-sm" style={{ color: overdue ? '#dc2626' : 'var(--theme-muted-text)' }}>
                    {task.isMilestone ? '—' : fmtDate(task.endDate)}
                  </span>
                </DataTableCell>

                <DataTableCell>
                  {deps.length === 0 ? (
                    <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {deps.slice(0, 2).map((dep, i) => {
                        const pred = taskById.get(dep.taskId);
                        const c = MODE_CHIP[dep.mode];
                        return (
                          <span
                            key={`${dep.taskId}-${i}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                            style={{ fontSize: 10, backgroundColor: c.bg, color: c.fg }}
                            title={`${pred?.ticket?.title ?? 'Tarefa'} · ${MODE_LABEL[dep.mode]}`}
                          >
                            <Icon name="link" className="w-2.5 h-2.5" />
                            {MODE_LABEL[dep.mode]}
                          </span>
                        );
                      })}
                      {deps.length > 2 && (
                        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                          +{deps.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </DataTableCell>

                <DataTableCell>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full font-medium"
                      style={{ fontSize: 11, backgroundColor: chip.bg, color: chip.fg }}
                    >
                      {chip.label}
                    </span>
                    {task.blocked && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium"
                        style={{ fontSize: 10, backgroundColor: 'rgba(239, 68, 68, 0.14)', color: '#b91c1c' }}
                        title="Tarefa bloqueada por dependência"
                      >
                        <Icon name="lock" className="w-2.5 h-2.5" />
                        Bloqueada
                      </span>
                    )}
                  </div>
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </DataTableBody>
        {/* Legenda dos modos — rodapé DENTRO do card da tabela */}
        <tfoot>
          <tr>
            <td colSpan={8} style={{ borderTop: '1px solid var(--theme-card-border)', padding: '12px 16px' }}>
              <div className="flex flex-wrap items-center gap-3" style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}>
                <span className="font-medium">Modos de dependência:</span>
                {(['inform', 'warn', 'block'] as const).map((m) => (
                  <span key={m} className="inline-flex items-center gap-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm"
                      style={{ backgroundColor: MODE_CHIP[m].fg }}
                    />
                    {MODE_LABEL[m]}
                  </span>
                ))}
              </div>
            </td>
          </tr>
        </tfoot>
      </DataTable>
    </div>
  );
};

ProjectTasksTable.displayName = 'ProjectTasksTable';
